import type { CropViewportState } from "./cropImageFile";
import { getCropViewportDimensions } from "./cropImageFile";

export type CropViewportSize = { width: number; height: number };

/** Shape stored in DB jsonb / social_custom (matches social icons). */
export type PersistedImageCrop = {
  cropState: CropViewportState;
  cropViewport: CropViewportSize;
};

export function parseStoredCropState(value: unknown): CropViewportState | null {
  if (!value || typeof value !== "object") return null;
  const zoom = Number((value as CropViewportState).zoom);
  const x = Number((value as CropViewportState).position?.x);
  const y = Number((value as CropViewportState).position?.y);
  if (![zoom, x, y].every((n) => Number.isFinite(n))) return null;
  if (zoom < 1) return null;
  return { zoom, position: { x, y } };
}

export function parseStoredCropViewport(value: unknown): CropViewportSize | null {
  if (!value || typeof value !== "object") return null;
  const width = Number((value as CropViewportSize).width);
  const height = Number((value as CropViewportSize).height);
  if (![width, height].every((n) => Number.isFinite(n) && n > 0)) return null;
  return { width, height };
}

export function isCropViewportCompatible(
  stored: unknown,
  expected: CropViewportSize
): boolean {
  if (stored == null) return true; // legacy rows without viewport metadata
  const parsed = parseStoredCropViewport(stored);
  if (!parsed) return false;
  return parsed.width === expected.width && parsed.height === expected.height;
}

export function buildPersistedImageCrop(
  cropState: CropViewportState | null | undefined,
  viewport: CropViewportSize
): PersistedImageCrop | null {
  if (!cropState) return null;
  const parsed = parseStoredCropState(cropState);
  if (!parsed) return null;
  return {
    cropState: parsed,
    cropViewport: {
      width: viewport.width,
      height: viewport.height,
    },
  };
}

/**
 * Read cropState from a jsonb `image_crop` column or a social_custom-style object
 * that has `{ cropState, cropViewport }`.
 */
export function resolvePersistedCropState(
  imageCropOrEntry: unknown,
  expectedViewport: CropViewportSize
): CropViewportState | null {
  if (!imageCropOrEntry || typeof imageCropOrEntry !== "object") return null;
  const record = imageCropOrEntry as Record<string, unknown>;
  // jsonb column shape: { cropState, cropViewport }
  const cropState = record.cropState ?? record.crop_state ?? imageCropOrEntry;
  const cropViewport = record.cropViewport ?? record.crop_viewport;
  if (!isCropViewportCompatible(cropViewport, expectedViewport)) {
    return null;
  }
  // If the whole value is already a CropViewportState
  if ("zoom" in record && "position" in record && !("cropState" in record)) {
    return parseStoredCropState(record);
  }
  return parseStoredCropState(cropState);
}

export function viewportFromPreset(preset: {
  aspectRatio: number;
  viewportWidth?: number;
  viewportHeight?: number;
}): CropViewportSize {
  const { viewportWidth, viewportHeight } = getCropViewportDimensions(
    preset.aspectRatio,
    {
      width: preset.viewportWidth,
      height: preset.viewportHeight,
    }
  );
  return { width: viewportWidth, height: viewportHeight };
}

export function isMissingImageCropColumnError(error: any): boolean {
  const message = String(
    error?.message || error?.details || error?.hint || ""
  ).toLowerCase();
  return (
    message.includes("image_crop") ||
    message.includes("schema cache") ||
    message.includes("could not find")
  );
}

export type LinkCropLayout = "line" | "grid";

export type LinkLayoutCropEntry = PersistedImageCrop & {
  /** Cropped display URL for this layout only. */
  image_url?: string | null;
};

export type LinkImageCropByLayout = {
  line?: LinkLayoutCropEntry;
  grid?: LinkLayoutCropEntry;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/** Legacy flat `{ cropState, cropViewport }` (no line/grid keys). */
export function isFlatPersistedImageCrop(value: unknown): boolean {
  if (!isRecord(value)) return false;
  if ("line" in value || "grid" in value) return false;
  return (
    "cropState" in value ||
    "crop_state" in value ||
    ("zoom" in value && "position" in value)
  );
}

export function isKeyedLinkImageCrop(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return isRecord(value.line) || isRecord(value.grid);
}

/**
 * Resolve the layout entry from `image_crop`.
 * Flat legacy JSON is treated as belonging to `layout` (current component view).
 */
export function getLinkLayoutCropEntry(
  imageCrop: unknown,
  layout: LinkCropLayout
): LinkLayoutCropEntry | null {
  if (!isRecord(imageCrop)) return null;

  if (isKeyedLinkImageCrop(imageCrop)) {
    const entry = imageCrop[layout];
    if (!isRecord(entry)) return null;
    const cropState = parseStoredCropState(
      entry.cropState ?? entry.crop_state ?? entry
    );
    const cropViewport = parseStoredCropViewport(
      entry.cropViewport ?? entry.crop_viewport
    );
    if (!cropState || !cropViewport) return null;
    const imageUrl =
      typeof entry.image_url === "string" ? entry.image_url : null;
    return { cropState, cropViewport, image_url: imageUrl };
  }

  if (isFlatPersistedImageCrop(imageCrop)) {
    // Respect existing rows: assign flat crop to the active component layout.
    const cropState = parseStoredCropState(
      (imageCrop as any).cropState ??
        (imageCrop as any).crop_state ??
        imageCrop
    );
    const cropViewport = parseStoredCropViewport(
      (imageCrop as any).cropViewport ?? (imageCrop as any).crop_viewport
    );
    if (!cropState) return null;
    return {
      cropState,
      cropViewport: cropViewport ?? { width: 0, height: 0 },
      image_url: null,
    };
  }

  return null;
}

/** Crop zoom/pan for the cropper — null if viewport does not match this layout. */
export function resolveLinkLayoutCropState(
  imageCrop: unknown,
  layout: LinkCropLayout,
  expectedViewport: CropViewportSize
): CropViewportState | null {
  const entry = getLinkLayoutCropEntry(imageCrop, layout);
  if (!entry) return null;
  if (
    entry.cropViewport.width > 0 &&
    entry.cropViewport.height > 0 &&
    !isCropViewportCompatible(entry.cropViewport, expectedViewport)
  ) {
    // Flat crop from the other layout after a view switch — start fresh from original.
    return null;
  }
  return entry.cropState;
}

/**
 * Display URL for the active layout.
 * - Keyed + has layout URL → that URL
 * - Flat legacy → `fallbackImageUrl` (active `image_url`)
 * - Keyed but missing this layout → keep `fallbackImageUrl` (other layout's
 *   crop) until ensureUserLinksHaveLayoutCrop writes the new one. Preferring
 *   `originalImageUrl` here flashes the full uncropped photo and looks broken.
 */
export function getLinkLayoutDisplayUrl(
  imageCrop: unknown,
  layout: LinkCropLayout,
  fallbackImageUrl?: string | null,
  originalImageUrl?: string | null
): string | null {
  const entry = getLinkLayoutCropEntry(imageCrop, layout);
  if (entry?.image_url) return entry.image_url;

  if (isFlatPersistedImageCrop(imageCrop) || !isKeyedLinkImageCrop(imageCrop)) {
    return fallbackImageUrl || null;
  }

  // Keyed store without this layout yet — hold the current display.
  return fallbackImageUrl || originalImageUrl || null;
}

/** True when a new non-empty original URL replaces a different prior original. */
export function hasLinkOriginalImageChanged(
  previousOriginalUrl: string | null | undefined,
  nextOriginalUrl: string | null | undefined
): boolean {
  const prev = String(previousOriginalUrl || "").trim();
  const next = String(nextOriginalUrl || "").trim();
  return Boolean(next) && next !== prev;
}

/**
 * First candidate URL that differs from the active display URL (true original).
 * Used so cropState is only restored against the uncropped bitmap.
 */
export function getDistinctLinkOriginalUrl(
  currentDisplayUrl: string,
  ...candidates: Array<string | null | undefined>
): string | null {
  const current = String(currentDisplayUrl || "").trim();
  for (const candidate of candidates) {
    const url = String(candidate || "").trim();
    if (url && url !== current) return url;
  }
  return null;
}

export function oppositeLinkCropLayout(
  layout: LinkCropLayout
): LinkCropLayout {
  return layout === "grid" ? "line" : "grid";
}

export type MergeLinkLayoutImageCropOptions = {
  /**
   * When the original image URL changes, drop the opposite layout's entry
   * (its crop/display was for the old bitmap).
   */
  previousOriginalUrl?: string | null;
  nextOriginalUrl?: string | null;
};

/**
 * Merge a layout entry into `image_crop` without wiping the other layout.
 * Flat legacy is lifted under `layout` when it matches that viewport; otherwise
 * it is kept under the other layout so a view switch does not destroy it.
 *
 * When `previousOriginalUrl` / `nextOriginalUrl` show the original changed,
 * the opposite layout key is discarded.
 */
export function mergeLinkLayoutImageCrop(
  existing: unknown,
  layout: LinkCropLayout,
  nextEntry: LinkLayoutCropEntry | null,
  expectedViewport?: CropViewportSize,
  options?: MergeLinkLayoutImageCropOptions
): LinkImageCropByLayout | null {
  const clearOtherLayout = hasLinkOriginalImageChanged(
    options?.previousOriginalUrl,
    options?.nextOriginalUrl
  );
  const otherLayout = oppositeLinkCropLayout(layout);
  let base: LinkImageCropByLayout = {};

  if (isKeyedLinkImageCrop(existing)) {
    const keyed = existing as LinkImageCropByLayout & {
      coverFrame?: unknown;
    };
    if (keyed.line) base.line = { ...keyed.line };
    if (keyed.grid) base.grid = { ...keyed.grid };
  } else if (isFlatPersistedImageCrop(existing)) {
    const lifted = getLinkLayoutCropEntry(existing, layout);
    if (lifted) {
      const flatMatchesLayout =
        !expectedViewport ||
        lifted.cropViewport.width === 0 ||
        isCropViewportCompatible(lifted.cropViewport, expectedViewport);
      if (flatMatchesLayout) {
        base = { [layout]: lifted };
      } else if (!clearOtherLayout) {
        // Keep flat under the other layout only when original is unchanged.
        base = { [otherLayout]: lifted };
      }
    }
  }

  // Preserve website-editor cover frame (object-position / zoom) across crop merges.
  const existingCover =
    existing && typeof existing === "object" && !Array.isArray(existing)
      ? (existing as Record<string, unknown>).coverFrame
      : undefined;
  if (existingCover && typeof existingCover === "object") {
    (base as Record<string, unknown>).coverFrame = existingCover;
  }

  if (clearOtherLayout) {
    delete base[otherLayout];
  }

  if (nextEntry == null) {
    delete base[layout];
  } else {
    base[layout] = {
      ...base[layout],
      ...nextEntry,
    };
  }

  const hasCover = Boolean((base as Record<string, unknown>).coverFrame);
  if (!base.line && !base.grid && !hasCover) return null;
  return base;
}
